import YouTubeIcon from '@mui/icons-material/YouTube';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import {
  Box, Fab, TextField, Typography, useMediaQuery,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useContext } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';
import { TranscriptionDialogContext } from '../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import type { ITheme } from '../../../theme';
import { extractYoutubeId, YOU_TUBE_URL_REGEX } from '../../../utils/youtubeHelper';
import YoutubePreview from '../YoutubePreview';
import { TrackingCategory, TrackingTranscriptionParams, trackingService } from '../../../services';

const useStyles = makeStyles()((theme: ITheme) => ({
  youtubeTextfield: {
    marginTop: 10,
    marginLeft: 30,
    color: '#fff',
    width: 'calc(100% - 30px - 90px) !important',
    '@media screen and (max-width:767px)': {
      marginLeft: 0,
      marginBottom: 10,
      width: '100% !important',
    },
  },
  youtubeIcon: {
    fontSize: '5em !important',
    color: '#fff',
  },
  transcribeButton: {
    marginTop: '15px !important',
    backgroundColor: `${theme.palette.primary.main}`,
    '&:hover': {
      // override elementor
      backgroundColor: `${theme.palette.primary.main}a6`,
      color: '#fff',
    },
  },
}));

export interface YoutubeInputComponentProps {
  openDialog: () => void;
  label: string;
  location?: string;
}

const YoutubeInputComponent: React.FC<YoutubeInputComponentProps> = ({
  openDialog,
  label,
  location,
}) => {
  const [showVideo, setShowVideo] = React.useState(false);
  const [isInvalidVideo, setIsInvalidVideo] = React.useState(true);
  const [showYoutubeError, setShowYoutubeError] = React.useState(false);
  const { setYouTubeUrl, youTubeUrl } = useContext(TranscriptionDialogContext);
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('xs'));
  const { classes } = useStyles();

  const validationSchema = Yup.object({
    url: Yup.string()
      .matches(YOU_TUBE_URL_REGEX, t('home.input.url_not_valid'))
      .required(t('home.input.youtube_url')),
  });

  const handleSubmit = async () => {
    if (!youTubeUrl) {
      return;
    }

    const trackingValues = {
      input: TrackingTranscriptionParams.input.youtube,
      location,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.start,
      {
        ...trackingValues,
      },
    );
    // console.log(TrackingTranscriptionParams.action.start, { ...trackingValues });

    openDialog();
  };

  const formik = useFormik({
    initialValues: {
      url: youTubeUrl || '',
    },
    validationSchema,
    onSubmit: () => handleSubmit(),
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    setShowVideo(true);
    const id = extractYoutubeId(event.target.value);
    setYouTubeUrl(
      id !== null
        ? `https://youtube.com/watch?v=${extractYoutubeId(event.target.value)}`
        : event.target.value,
    );
  };

  return (
    <StyledEngineProvider injectFirst>
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <div>
          {showYoutubeError && (
            <Box height={64} display="flex" justifyContent="center" alignItems="center">
              <Typography color="error" variant="h6">
                {t('home.input.duration_too_long')}
              </Typography>
            </Box>
          )}
          {showVideo && (
            <YoutubePreview
              youtubeUrl={formik.values.url}
              onError={() => {
                setShowVideo(false);
                setIsInvalidVideo(true);
                setShowYoutubeError(false);
              }}
              onReady={() => {
                setIsInvalidVideo(false);
                setShowYoutubeError(false);
              }}
              onDurationTooLong={() => {
                setShowVideo(false);
                setIsInvalidVideo(true);
                setShowYoutubeError(true);
              }}
            />
          )}
          <div>
            {(showVideo && isMobile) || <YouTubeIcon className={classes.youtubeIcon} />}
            <TextField
              name="url"
              className={classes.youtubeTextfield}
              value={formik.values.url}
              onChange={handleChange}
              label={label}
              error={!!formik.errors.url}
              helperText={formik.errors.url}
              type="search"
              variant="outlined"
            />
          </div>
        </div>
        <Fab
          id="btn_transcribe_youtube"
          data-testid="transcribe-btn"
          disabled={isInvalidVideo || !!formik.errors.url}
          type="submit"
          color="primary"
          variant="extended"
          size="medium"
          className={classes.transcribeButton}
        >
          <AutoFixNormalIcon style={{ marginRight: 10 }} />
          {t('home.input.transcribe')}
        </Fab>
      </form>
    </StyledEngineProvider>
  );
};

export default YoutubeInputComponent;
